// In-memory credential records for VITE_CREDENTIALING_MOCKS, seeded from the
// Personnel design (Maria Lopez, id 5). Fictional data only. ID numbers are
// kept in full here and leave only masked, or through revealRecordNumber().
import {
  ApprovalStatus,
  CredentialFile,
  CredentialKind,
  CredentialRecord,
  CredentialRecordsRequest,
  MonthlyCheck,
  PayerApproval,
  ReadDocumentRequest,
  ReadDocumentResult,
  SaveCredentialRecordRequest,
  SaveMonthlyCheckRequest,
  ValidityFlag,
} from '@src/types/personnel';
import { mockApiError } from '../credentialingContract';

const PAYERS = ['IEHP Health Plan', 'LA Care Health Plan', 'Molina Healthcare'];

const REJECTION_REASONS: Record<string, string> = {
  'ADA sensitivity training':
    'The certificate is unreadable. Please upload a clearer copy.',
  'Special driver permit':
    'The permit number on the document doesn’t match the number entered. Please correct it or upload a clearer copy.',
};

// the types whose files often show no expiration date (WF-D2 rules)
const NO_EXPIRATION_TYPES = [
  'Background check',
  'Drug test',
  'Sex offender registry',
  'OIG/SAM exclusion',
  'Fingerprinting',
  'Wheelchair training',
  'Gurney training',
  'ADA sensitivity training',
  'Securement',
];

const approvals = (
  type: string,
  ...statuses: Array<ApprovalStatus>
): Array<PayerApproval> =>
  statuses.map((status, index) => ({
    payerId: index + 1,
    payerName: PAYERS[index],
    status,
    ...(status === 'Rejected' && {
      decidedAt: index === 0 ? '10/10/2026' : '09/18/2026',
      rejectionReason:
        REJECTION_REASONS[type] ?? 'Please upload a clearer copy.',
    }),
  }));

const file = (name: string, size: number, slot?: string): CredentialFile => ({
  id: `${name}-${size}`,
  name,
  size,
  type: name.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg',
  slot,
});

type MockRecord = CredentialRecord & { fullNumber?: string };

let nextRecordId = 100;

const record = (
  kind: CredentialKind,
  type: string,
  values: Record<string, string | null>,
  statuses: Array<ApprovalStatus>,
  validity: ValidityFlag | null = null,
  files: Array<CredentialFile> = [file(`${kind}_document.pdf`, 640_000)],
  extra: Partial<MockRecord> = {}
): MockRecord => ({
  id: nextRecordId++,
  personnelId: 5,
  kind,
  type,
  values,
  files,
  validity,
  approvals: approvals(type, ...statuses),
  ...extra,
});

const mask = (value?: string | null) =>
  value ? `***${value.slice(-4)}` : null;

const idRecord = (
  type: string,
  fullNumber: string,
  values: Record<string, string | null>,
  statuses: Array<ApprovalStatus>,
  validity: ValidityFlag | null = null
) =>
  record(
    'identification',
    type,
    { ...values, number: mask(fullNumber) },
    statuses,
    validity,
    [
      file('id_front.jpg', 1_400_000, 'front'),
      file('id_back.jpg', 1_200_000, 'back'),
    ],
    { fullNumber }
  );

const monthly = (
  checkDate: string,
  result: MonthlyCheck['result'],
  approval: ApprovalStatus,
  validity: ValidityFlag | null = null
): MonthlyCheck => ({
  approval,
  checkDate,
  id: nextRecordId++,
  result,
  validity,
  vendor: 'Sterling',
});

const A: ApprovalStatus = 'Approved';
const P: ApprovalStatus = 'Pending Review';
const R: ApprovalStatus = 'Rejected';
const N: ApprovalStatus = 'Not sent yet';

// prettier-ignore
let records: Array<MockRecord> = [
  idRecord('State ID', 'S11205530', { issuingState: 'California', class: null, issueDate: '08/01/2026', expirationDate: '08/01/2032' }, [P, P, P]),
  idRecord("Driver's License", 'D0084821', { issuingState: 'California', class: 'Class C', issueDate: '06/14/2023', expirationDate: '06/14/2028' }, [A, A, A]),
  idRecord("Driver's License", 'D0077730', { issuingState: 'California', class: 'Class C', issueDate: '06/14/2018', expirationDate: '06/14/2023' }, [A, A, A], 'Expired'),

  record('screening', 'DOT physical', { vendor: 'Concentra', completionDate: '10/05/2026', expirationDate: '10/05/2028', result: 'Passed' }, [P, P, P]),
  record('screening', 'Background check', { vendor: 'Sterling', completionDate: '01/10/2026', expirationDate: null, result: 'Passed' }, [A, A, A]),
  record('screening', 'Drug test', { vendor: 'Quest Diagnostics', completionDate: '01/12/2026', expirationDate: null, result: 'Passed' }, [A, A, P]),
  record('screening', 'MVR', { vendor: 'Sterling', completionDate: '01/15/2026', expirationDate: '01/15/2027', result: 'Passed' }, [A, A, A]),
  record('screening', 'TB test', { vendor: 'Concentra', completionDate: '11/02/2025', expirationDate: '11/02/2026', result: 'Passed' }, [A, A, A], 'Expiring Soon'),
  record('screening', 'OIG/SAM exclusion', { vendor: 'Sterling', completionDate: '01/08/2026', expirationDate: null, result: 'Passed' }, [A, A, A], null, undefined, {
    monthlyChecks: [
      monthly('10/08/2026', 'Not run', 'Not sent yet', 'Overdue'),
      monthly('09/08/2026', 'Passed', 'Approved automatically'),
      monthly('08/08/2026', 'Passed', 'Approved automatically'),
      monthly('07/08/2026', 'Passed', 'Approved automatically'),
    ],
  }),

  record('training', 'HIPAA', { provider: 'Relias', completionDate: '02/01/2026', expirationDate: '02/01/2027', certificateNumber: 'HIP-55821' }, [A, A, A]),
  record('training', 'Defensive driving', { provider: 'Smith System', completionDate: '01/20/2026', expirationDate: '01/20/2028', certificateNumber: 'SSD-30418' }, [A, A, A]),
  record('training', 'PASS', { provider: 'CTAA', completionDate: '12/05/2024', expirationDate: '12/05/2026', certificateNumber: 'PASS-77102' }, [A, A, A], 'Expiring Soon'),
  record('training', 'CPR/First Aid', { provider: 'American Red Cross', completionDate: '03/10/2024', expirationDate: '03/10/2026', certificateNumber: 'ARC-9C41K' }, [A, A, A], 'Expired'),
  record('training', 'Wheelchair training', { provider: 'Q’Straint', completionDate: '10/09/2026', expirationDate: null, certificateNumber: 'QS-41907' }, [P, P, N]),
  record('training', 'ADA sensitivity training', { provider: 'Relias', completionDate: '09/28/2026', expirationDate: null, certificateNumber: 'ADA-11872' }, [R, P, P]),

  record('permit', 'TNC or taxi', { number: 'PSD-20931', issuingAuthority: 'City of Pasadena', state: 'California', issueDate: '04/01/2026', expirationDate: '03/31/2027' }, [A, A, A]),
  record('permit', 'Special driver permit', { number: 'SDP-118204', issuingAuthority: 'California DMV', state: 'California', issueDate: '09/15/2026', expirationDate: '09/15/2028' }, [P, R, P]),
];

const findRecord = (id: number): MockRecord => {
  const found = records.find((item) => item.id === id);
  if (!found) throw mockApiError(404, 'Record not found');
  return found;
};

const toResponse = ({ fullNumber: _fullNumber, ...rest }: MockRecord) => rest;

const isLocked = ({ approvals: list }: CredentialRecord) =>
  list.some(({ status }) =>
    ['Approved', 'Approved automatically', 'In Review'].includes(status)
  );

const READ_SAMPLES: Record<
  Exclude<CredentialKind, 'identification'>,
  { values: Record<string, string>; uncertain: Array<string> }
> = {
  permit: {
    uncertain: [],
    values: {
      expirationDate: '09/30/2027',
      issueDate: '10/01/2026',
      issuingAuthority: 'City of Pasadena',
      number: 'LOP-58213',
      state: 'California',
    },
  },
  screening: {
    uncertain: ['vendor'],
    values: {
      completionDate: '01/12/2026',
      expirationDate: '01/12/2028',
      result: 'Passed',
      vendor: 'Quest Diagnostics',
    },
  },
  training: {
    uncertain: ['certificateNumber'],
    values: {
      certificateNumber: 'ARC-7Q22M',
      completionDate: '10/14/2026',
      expirationDate: '10/14/2028',
      provider: 'American Red Cross',
    },
  },
};

export const revealRecordNumber = (recordId: number): string => {
  const { fullNumber } = findRecord(recordId);
  if (!fullNumber) throw mockApiError(404, 'Nothing to reveal');
  return fullNumber;
};

export const credentialMocks = {
  list: ({ kind, personnelId }: CredentialRecordsRequest) =>
    records
      .filter((item) => item.kind === kind && item.personnelId === personnelId)
      .map(toResponse),

  // "blank" in a file name plays an unreadable file, "mismatch" a warning
  read: ({
    files,
    kind,
    phoneFiles = [],
    type,
  }: ReadDocumentRequest): ReadDocumentResult => {
    const names = [
      ...files.map(({ file: picked }) => picked.name),
      ...phoneFiles.map(({ name }) => name),
    ].map((name) => name.toLowerCase());
    const warnings = names.some((name) => name.includes('mismatch'))
      ? [
          kind === 'identification'
            ? {
                title: 'Front and back don’t match',
                message:
                  'The name on the front (Maria Lopes) doesn’t match the barcode (Maria Lopez). The front and back may be from different IDs.',
              }
            : {
                title: 'Name doesn’t match the profile',
                message:
                  'The name on the document doesn’t match this personnel member’s profile. Check it is their document before saving.',
              },
        ]
      : [];

    if (names.some((name) => name.includes('blank'))) {
      return {
        readOnly: [],
        source: 'none',
        uncertain: [],
        values: {},
        warnings,
      };
    }

    if (kind === 'identification') {
      const values: Record<string, string> = {
        expirationDate: '06/14/2028',
        issueDate: '06/14/2023',
        issuingState: 'California',
        number: 'D4821903',
        ...(type === "Driver's License" && { class: 'Class C' }),
      };
      const hasBack = [...files, ...phoneFiles].some(
        ({ slot }) => slot === 'back'
      );

      return hasBack
        ? {
            readOnly: Object.keys(values),
            source: 'barcode',
            uncertain: [],
            values,
            warnings,
          }
        : {
            readOnly: [],
            source: 'ocr',
            uncertain: ['number'],
            values,
            warnings,
          };
    }

    const sample = READ_SAMPLES[kind];
    const noExpiration = NO_EXPIRATION_TYPES.includes(type);
    const { expirationDate, ...withoutExpiration } = sample.values;

    return {
      noExpiration,
      readOnly: [],
      source: 'ocr',
      uncertain: sample.uncertain,
      values: noExpiration
        ? withoutExpiration
        : { ...withoutExpiration, expirationDate },
      warnings,
    };
  },

  save: ({
    id,
    kind,
    newFiles,
    personnelId,
    phoneFiles = [],
    removedFileIds,
    type,
    values,
  }: SaveCredentialRecordRequest) => {
    const number = values.number;
    const isFullNumber = !!number && !number.startsWith('***');

    if (kind === 'identification' && isFullNumber) {
      const taken = records.some(
        (item) =>
          item.id !== id &&
          item.personnelId !== personnelId &&
          item.fullNumber === number
      );
      // the seeded people hold D1180312 (Marcus Allen) at this contractor
      if (taken || number === 'D1180312') {
        throw mockApiError(
          409,
          'This license number already belongs to another personnel member at your contractor.'
        );
      }
    }

    const addedFiles: Array<CredentialFile> = [
      ...newFiles.map(({ file: picked, slot, source }, index) => ({
        ...file(picked.name, picked.size, slot),
        id: `new-${Date.now()}-${index}`,
        source,
        type: picked.type,
      })),
      // phone files are already with the server; they come by id
      ...phoneFiles.map((phoneFile) => ({
        ...phoneFile,
        source: 'phone' as const,
      })),
    ];

    if (id) {
      const current = findRecord(id);
      if (isLocked(current)) throw mockApiError(423, 'This record is locked');

      const updated: MockRecord = {
        ...current,
        values: {
          ...current.values,
          ...values,
          ...(kind === 'identification' && {
            number: isFullNumber ? mask(number) : current.values.number,
          }),
        },
        fullNumber: isFullNumber ? (number ?? undefined) : current.fullNumber,
        files: [
          ...current.files.filter(
            ({ id: fileId }) => !removedFileIds.includes(fileId)
          ),
          ...addedFiles,
        ],
        // an edit after a rejection sends the record again (ST-D2 5 → 2)
        approvals: current.approvals.map((approval) =>
          approval.status === 'Rejected'
            ? { ...approval, status: 'Pending Review', rejectionReason: null }
            : approval
        ),
      };

      records = records.map((item) => (item.id === id ? updated : item));
      return toResponse(updated);
    }

    const created: MockRecord = {
      ...record(
        kind,
        type,
        kind === 'identification'
          ? { ...values, number: mask(number) }
          : values,
        ['Pending Review', 'Pending Review', 'Pending Review'],
        null,
        addedFiles
      ),
      fullNumber: kind === 'identification' ? (number ?? undefined) : undefined,
      personnelId,
    };

    records = [created, ...records];
    return toResponse(created);
  },

  saveMonthlyCheck: ({
    checkDate,
    result,
    screeningId,
    vendor,
  }: SaveMonthlyCheckRequest): MonthlyCheck => {
    const screening = findRecord(screeningId);
    const check: MonthlyCheck = {
      // a clear check is approved automatically; a finding goes to the payers
      approval:
        result === 'Passed' ? 'Approved automatically' : 'Pending Review',
      checkDate,
      id: nextRecordId++,
      result,
      validity: null,
      vendor,
    };

    const monthlyChecks = [
      check,
      ...(screening.monthlyChecks ?? []).filter(
        ({ result: done }) => done !== 'Not run'
      ),
    ];
    records = records.map((item) =>
      item.id === screeningId ? { ...item, monthlyChecks } : item
    );
    return check;
  },
};
